import { bayer, hash01 } from "../../intro/pixel_raster"

/**
 * The chapter title card's picture: one view over the forest the camp is hidden in, seen from a
 * hillside at night, in the login intro's pixel art (its sky ramp, moon and ordered dithering).
 *
 * Nothing in it acts out the chapter -- no people, creatures or props; the words do that. The only
 * thing that changes from card to card is the HOUR: dusk at chapter one, deep night through the
 * middle chapters, first light at chapter nine, the moon crossing from left to right as the story
 * goes on and never standing behind the words. The camp is only a point of firelight in the trees
 * and a thread of smoke.
 *
 * Coordinates are art pixels (ART_PX logical pixels each) in the GUI's authored 800 x 460 frame,
 * so 400 x 230 of them; a wider or taller screen just shows more of the same sky and ridges.
 * Pure: no DOM, no pixi. Everything that moves is a function of the clock, so `paint` at the same
 * time always draws the same frame.
 */

export type RGB = [number, number, number]

const hex = (h: number): RGB => [(h >> 16) & 0xff, (h >> 8) & 0xff, h & 0xff]
const mixc = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const byte = (v: number) => (v <= 0 ? 0 : v >= 255 ? 255 : Math.round(v))
/** Packed the way a Uint32Array over RGBA bytes holds it on a little-endian machine (pixel_raster's rgb). */
export const pack = (c: RGB, alpha: number = 255): number => ((alpha << 24) | (byte(c[2]) << 16) | (byte(c[1]) << 8) | byte(c[0])) >>> 0
export const unpack = (p: number): RGB => [p & 0xff, (p >>> 8) & 0xff, (p >>> 16) & 0xff]

/** A steps-and-seams gradient: each band dithered into the next only along its last `soft` share. */
const banded = (colors: RGB[], g: number, x: number, y: number, soft: number = 0.4): RGB => {
    const top = colors.length - 1
    if (g <= 0) return colors[0]
    if (g >= top) return colors[top]
    const i = Math.floor(g)
    const f = clamp01((g - i - (1 - soft)) / soft)
    return colors[bayer(x, y) < f ? i + 1 : i]
}
const rampOf = (keys: RGB[], n: number): RGB[] =>
    Array.from({ length: n }, (_, i) => {
        const t = (i / (n - 1)) * (keys.length - 1)
        const k = Math.min(keys.length - 2, Math.floor(t))
        return mixc(keys[k], keys[k + 1], t - k).map(Math.round) as RGB
    })

// ---- the hours --------------------------------------------------------------------------------

export interface Hour {
    name: string
    /** Sky colours from the top of the screen to the horizon. */
    sky: number[]
    /** An afterglow or a dawn low on one side: colour, its x, its strength. */
    glow?: [number, number, number]
    /** The share of the stars that are out. */
    stars: number
    /** Where the moon stands, in art pixels. */
    moon: [number, number]
    /** The haze lying in the hollows. */
    mist: number
    /** What the moon (or the dawn) rims the nearest trees with. */
    rim: number
}

/** One per chapter, in LPC ramps. The moon keeps clear of the words (above the title on the left, then on the right). */
export const HOURS: Hour[] = [
    { name: "Dusk", sky: [0x0c111d, 0x1d1a2e, 0x2e2533, 0x523745], glow: [0x904237, 30, 0.75], stars: 0.28, moon: [62, 100], mist: 0x6d5f74, rim: 0x959fc5 },
    { name: "Nightfall", sky: [0x090c15, 0x141d31, 0x2e2533, 0x3d3748], glow: [0x543873, 40, 0.5], stars: 0.55, moon: [48, 74], mist: 0x524f69, rim: 0x8188be },
    { name: "Evening", sky: [0x06080e, 0x0c111d, 0x141d31, 0x2a3044], glow: [0x2f1c40, 40, 0.35], stars: 0.8, moon: [40, 48], mist: 0x46516a, rim: 0x8aaaab },
    { name: "Night", sky: [0x06080e, 0x0c111d, 0x141d31, 0x1a253c], stars: 1, moon: [50, 24], mist: 0x44516c, rim: 0xa9c9ca },
    { name: "Midnight", sky: [0x05070c, 0x090c15, 0x101726, 0x172036], stars: 1, moon: [352, 22], mist: 0x3c4862, rim: 0xc8e8e8 },
    { name: "Past midnight", sky: [0x05070c, 0x090c15, 0x0f1624, 0x162034], stars: 1, moon: [362, 44], mist: 0x48556e, rim: 0xa9c9ca },
    { name: "Small hours", sky: [0x06080e, 0x0b1018, 0x101a22, 0x15262c], stars: 0.9, moon: [366, 68], mist: 0x3e5455, rim: 0x8aaaab },
    { name: "Before dawn", sky: [0x090c15, 0x141d31, 0x2e2533, 0x46425d], glow: [0x586b90, 370, 0.4], stars: 0.5, moon: [358, 92], mist: 0x5d6577, rim: 0x748da4 },
    { name: "First light", sky: [0x0f1624, 0x1d2742, 0x3d3a55, 0x7a4a52], glow: [0xffa749, 372, 0.8], stars: 0.15, moon: [350, 100], mist: 0x7e7088, rim: 0xba6c4c },
]

/** Chapters past the last hour keep the last one. */
export const hourFor = (chapter: number): Hour => HOURS[Math.max(0, Math.min(HOURS.length - 1, Math.round(chapter) - 1))]

/** The authored frame, in art pixels. */
export const ART_W = 400
export const ART_H = 230
/** Where the words go: the moon never stands here. */
export const WORDS_BOX = { x0: 60, x1: 340, y0: 8, y1: 90 }

const HORIZON_Y = 124
const LAKE_Y = 137
const FOREST = hex(0x080d0e)
const MOON_R = 9
const MOON_COLORS = { rim: hex(0xf1efe2), lit: hex(0xd8d4c2), mid: hex(0xb9b5a6), crater: hex(0xa9a698), term: hex(0x5f6074), dark: hex(0x262d42) }
const CRATERS: [number, number, number][] = [[2.5, -4, 2.2], [-0.5, 3, 1.6], [5, 2.5, 1.5], [3.5, 6.5, 1.1], [6.5, -3.5, 1.1], [-2.5, -6, 1]]

interface Ridge {
    base: number
    amp: number
    f: [number, number]
    /** Tree heights, and the gaps between them. */
    h: [number, number]
    gap: [number, number]
    /** From the mist (0) to the forest's black (1): nearer ridges are darker. */
    tone: number
    mist: number
    rim: number
    fire?: number
    near?: boolean
}
const RIDGES: Ridge[] = [
    { base: 128, amp: 3, f: [0.041, 0.097], h: [3, 7], gap: [3, 7], tone: 0.42, mist: 0.5, rim: 0 },
    { base: 160, amp: 4, f: [0.023, 0.061], h: [5, 12], gap: [4, 9], tone: 0.6, mist: 0.75, rim: 0 },
    { base: 177, amp: 6, f: [0.018, 0.049], h: [10, 22], gap: [5, 12], tone: 0.74, mist: 0.7, rim: 0, fire: 252 },
    { base: 198, amp: 7, f: [0.014, 0.041], h: [16, 34], gap: [8, 17], tone: 0.86, mist: 0.55, rim: 0.32 },
    { base: 218, amp: 4, f: [0.011, 0.034], h: [0, 0], gap: [0, 0], tone: 0.96, mist: 0, rim: 0.36, near: true },
]
const lineOf = (r: Ridge, i: number) => (x: number) =>
    Math.round(r.base + r.amp * (0.6 * Math.sin(x * r.f[0] + i * 1.7) + 0.4 * Math.sin(x * r.f[1] + i * 3.1)))

/** Layer ids: what is in front of what, for the entrance (farthest first) and the moving parts. */
export const LAYER_SKY = 0
const LAYER_MOUNTAINS = 1
const ridgeLayer = (i: number) => i + 2

/** A spruce's silhouette: the intro's stacked tiers, without its shading. */
const pineShape = (cx: number, by: number, h: number, seed: number, set: (x: number, y: number) => void, slim: number = 1) => {
    const tiers = h >= 26 ? 5 : h >= 16 ? 4 : h >= 9 ? 3 : 2
    const w = h * 0.3 * slim
    const top = by - h
    const bottom = by - Math.max(1, Math.round(h * 0.08))
    for (let y = bottom; y <= by; y++) set(cx, y)
    for (let j = tiers - 1; j >= 0; j--) {
        const yb = top + Math.round(((j + 1) / tiers) * (bottom - top))
        const yt = j === 0 ? top : top + Math.round((j / tiers) * (bottom - top)) - Math.max(1, Math.round(h * 0.12))
        const wb = w * (0.42 + (0.58 * (j + 1)) / tiers)
        for (let y = yt; y <= yb; y++) {
            const hw = wb * ((y - yt + 1) / (yb - yt + 1))
            const hl = Math.round(hw - (hash01(seed * 131 + y * 7 + j) < 0.3 ? 1 : 0))
            const hr = Math.round(hw - (hash01(seed * 17 + y * 3 + j * 5) < 0.3 ? 1 : 0))
            for (let x = cx - hl; x <= cx + hr; x++) set(x, y)
        }
    }
}

/** A broadleaf's silhouette: a few overlapping round masses on a short trunk. */
const roundShape = (cx: number, by: number, h: number, seed: number, set: (x: number, y: number) => void) => {
    const r = Math.max(2, h * 0.34)
    for (let y = by - Math.round(h * 0.3); y <= by; y++) set(cx, y)
    for (const [bx, byo] of [[0, -h + r], [-r * 0.7, -h + r * 1.5], [r * 0.75, -h + r * 1.45]]) {
        const rr = r * (0.8 + hash01(seed + bx * 7) * 0.35)
        for (let y = Math.floor(by + byo - rr); y <= by + byo + rr; y++) {
            for (let x = Math.floor(cx + bx - rr); x <= cx + bx + rr; x++) {
                const d = Math.hypot(x + 0.5 - cx - bx, (y + 0.5 - by - byo) * 1.1) + (hash01(x * 13 + y * 7 + seed) - 0.5) * 1.2
                if (d < rr) set(x, y)
            }
        }
    }
}

export interface ArtRect {
    x0: number
    y0: number
    w: number
    h: number
}

interface Star {
    x: number
    y: number
    color: RGB
    seed: number
}

export class NightView {
    readonly hour: Hour
    /** The finished picture, still. */
    readonly base: Uint32Array
    /** The sky alone, which a ridge shows until it has dissolved in. */
    readonly sky: Uint32Array
    readonly layer: Uint8Array
    /** The camp's firelight, in art pixels, and the ridge it is on. */
    readonly fire: { x: number; y: number; layer: number }
    readonly layers = ridgeLayer(RIDGES.length - 1) + 1
    private fireDistance?: Float32Array
    private ramp: RGB[]
    private stars: Star[] = []

    constructor(chapter: number, readonly rect: ArtRect) {
        this.hour = hourFor(chapter)
        this.ramp = rampOf(this.hour.sky.map(hex), 7)
        const n = rect.w * rect.h
        this.base = new Uint32Array(n)
        this.sky = new Uint32Array(n)
        this.layer = new Uint8Array(n)
        this.fire = { x: RIDGES[2].fire!, y: lineOf(RIDGES[2], 2)(RIDGES[2].fire!) + 3, layer: ridgeLayer(2) }
        this.paintSky()
        this.paintMilkyWay()
        this.paintStars()
        this.paintMoon()
        this.sky.set(this.base)
        this.paintMountains()
        this.paintRidges()
    }

    // ---- access ---------------------------------------------------------------------------------

    index = (x: number, y: number): number => {
        const i = x - this.rect.x0
        const j = y - this.rect.y0
        return i < 0 || j < 0 || i >= this.rect.w || j >= this.rect.h ? -1 : j * this.rect.w + i
    }
    private put = (x: number, y: number, c: RGB, layer: number) => {
        const i = this.index(x, y)
        if (i < 0) return
        this.base[i] = pack(c)
        this.layer[i] = layer
    }
    private at = (x: number, y: number): RGB => {
        const i = this.index(x, y)
        return i < 0 ? this.ramp[0] : unpack(this.base[i])
    }
    private forEach = (fn: (x: number, y: number) => void) => {
        const { x0, y0, w, h } = this.rect
        for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) fn(x, y)
    }

    // ---- the sky ----------------------------------------------------------------------------------

    skyAt = (x: number, y: number): RGB => {
        const g = (this.ramp.length - 1) * Math.pow(clamp01((y - 4) / (HORIZON_Y - 4)), 1.35)
        let c = banded(this.ramp, g, x, y)
        const glow = this.hour.glow
        if (glow) {
            const [color, gx, k] = glow
            const s = clamp01(1 - Math.hypot((x - gx) / 170, (y - HORIZON_Y - 4) / 46)) * k
            const steps = Math.floor(s * 4 + (bayer(x, y) - 0.5) * 0.9)
            if (steps > 0) c = mixc(c, hex(color), Math.min(4, steps) * 0.16)
        }
        return c
    }

    private paintSky = () => this.forEach((x, y) => this.put(x, y, this.skyAt(x, y), LAYER_SKY))

    /** For the deepest hours, the Milky Way: a haze and a drift of small stars, away from the moon. */
    private paintMilkyWay = () => {
        if (this.hour.stars < 0.8) return
        const leftMoon = this.hour.moon[0] < ART_W / 2
        const ax = leftMoon ? 400 : 0, ay = 118, bx = leftMoon ? 150 : 250, by = -10
        const len2 = (bx - ax) ** 2 + (by - ay) ** 2
        this.forEach((x, y) => {
            if (y >= HORIZON_Y) return
            const t = ((x - ax) * (bx - ax) + (y - ay) * (by - ay)) / len2
            if (t < 0 || t > 1.1) return
            const d = Math.hypot(x - (ax + (bx - ax) * t), y - (ay + (by - ay) * t)) / (16 + 6 * Math.sin(t * 9))
            const k = clamp01(1 - d) * Math.sin(Math.PI * clamp01(t)) * 0.9
            const steps = Math.floor(k * 3 + (bayer(x, y) - 0.5) * 0.9)
            if (steps > 0) this.put(x, y, mixc(this.at(x, y), hex(0x46516a), Math.min(3, steps) * 0.07), LAYER_SKY)
            if (hash01(x * 913 + y * 37) < 0.05 * k) this.put(x, y, mixc(this.at(x, y), hex(0xb1b9cf), 0.55), LAYER_SKY)
        })
    }

    private paintStars = () => {
        const [mx, my] = this.hour.moon
        const glow = this.hour.glow
        const { x0, w } = this.rect
        // Hashed by position in the authored frame's width, so a wide screen repeats rather than reshuffles.
        const count = Math.round(140 * (w / ART_W))
        for (let i = 0; i < count; i++) {
            if (hash01(i * 23 + 7) > this.hour.stars) continue
            const x = x0 + Math.floor(hash01(i * 7 + 1) * w)
            const y = Math.floor(hash01(i * 11 + 3) * 112)
            if (Math.hypot(x - mx, y - my) < 26) continue
            if (glow && Math.hypot((x - glow[1]) / 1.6, y - HORIZON_Y) < 120 * glow[2]) continue
            const b = hash01(i * 17)
            const color = b > 0.9 ? hex(0xe9e4d4) : b > 0.55 ? hex(0x8f97b0) : hex(0x4a5372)
            const fade = 0.35 + 0.65 * clamp01((HORIZON_Y - y) / 70)
            const c = mixc(this.at(x, y), color, fade)
            this.stars.push({ x, y, color: c, seed: i })
            this.put(x, y, c, LAYER_SKY)
            if (b > 0.975 && this.hour.stars > 0.7) {
                for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) this.put(x + dx, y + dy, mixc(this.at(x + dx, y + dy), hex(0x5d6684), 0.7), LAYER_SKY)
            }
        }
    }

    /** The intro's moon and its stepped halo. Low in the dusk and the dawn it is paler. */
    private paintMoon = () => {
        const [cx, cy] = this.hour.moon
        const r = MOON_R
        const pale = this.hour.name === "First light" ? 0.45 : this.hour.name === "Dusk" ? 0.25 : 0
        const halo = mixc(hex(0x7383ad), this.ramp[this.ramp.length - 1], pale)
        const reach = r + 16
        for (let y = Math.floor(cy - reach); y <= cy + reach; y++) {
            for (let x = Math.floor(cx - reach); x <= cx + reach; x++) {
                const d = Math.hypot(x + 0.5 - cx, y + 0.5 - cy) + (bayer(x, y) - 0.5) * 2.2
                const t = (d < r + 3.5 ? 0.15 : d < r + 8.5 ? 0.085 : d < r + 15 ? 0.04 : 0) * (1 - pale)
                if (t > 0) this.put(x, y, mixc(this.at(x, y), halo, t), LAYER_SKY)
            }
        }
        for (let y = Math.floor(cy - r - 1); y <= cy + r + 1; y++) {
            for (let x = Math.floor(cx - r - 1); x <= cx + r + 1; x++) {
                const dx = x + 0.5 - cx, dy = y + 0.5 - cy
                const d = Math.hypot(dx, dy)
                if (d > r + 0.3) continue
                const term = -0.38 * r * Math.sqrt(Math.max(0, 1 - (dy / r) * (dy / r)))
                let c: RGB
                if (dx < term - 0.6) c = MOON_COLORS.dark
                else if (dx < term + 0.6) c = MOON_COLORS.term
                else if (dx < term + 2.2) c = MOON_COLORS.mid
                else if (d > r - 1.1 && dx > 0) c = MOON_COLORS.rim
                else c = MOON_COLORS.lit
                if (c === MOON_COLORS.lit || c === MOON_COLORS.mid) {
                    for (const [ox, oy, cr] of CRATERS) if (Math.hypot(dx - ox * 0.7, dy - oy * 0.7) < cr * 0.75) c = MOON_COLORS.crater
                }
                if (pale) c = mixc(c, this.at(x, y), pale)
                this.put(x, y, c, LAYER_SKY)
            }
        }
    }

    // ---- the land ---------------------------------------------------------------------------------

    /** Is the moon (or the dawn) on the left? The nearest trees are rimmed on that side. */
    private rimFromLeft = () => this.hour.moon[0] < ART_W / 2 && !(this.hour.glow && this.hour.glow[1] > ART_W / 2 && this.hour.glow[2] > 0.6)

    /** A hazy range far off, one moonlit pixel along its crest so it reads as a single line. */
    private paintMountains = () => {
        const low = this.ramp[this.ramp.length - 1]
        const tone = mixc(low, hex(0x0c111d), 0.3)
        const lit = mixc(tone, hex(this.hour.rim), 0.22)
        const mist = hex(this.hour.mist)
        const ridge = (x: number) => {
            let v = 0
            for (const [f, a, ph] of [[0.012, 1, 0.3], [0.027, 0.55, 1.9], [0.061, 0.22, 4.1]]) v += a * (1 - Math.abs(Math.sin(x * f + ph)))
            return Math.round(122 - 16 * v)
        }
        const { x0, w } = this.rect
        for (let x = x0; x < x0 + w; x++) {
            const crest = Math.min(ridge(x), ridge(x - 1), ridge(x + 1))
            for (let y = ridge(x); y < LAKE_Y + 2; y++) {
                let c = y <= crest ? lit : tone
                const steps = Math.floor(clamp01((y - 106) / 22) * 4 + (bayer(x, y) - 0.5) * 0.9)
                if (steps > 0) c = mixc(c, mist, Math.min(4, steps) * 0.13)
                this.put(x, y, c, LAYER_MOUNTAINS)
            }
        }
    }

    /** The lake below the far forest: the sky and far shore upside down, darker, with faint ripples. */
    private paintLake = (layer: number) => {
        const deep = mixc(this.ramp[1], hex(0x0a0e18), 0.5)
        const mist = hex(this.hour.mist)
        const { x0, y0, w, h } = this.rect
        for (let y = Math.max(LAKE_Y, y0); y < y0 + h; y++) {
            for (let x = x0; x < x0 + w; x++) {
                let c = this.at(x, 2 * LAKE_Y - y - 1)
                c = mixc(c, deep, clamp01(0.16 + (y - LAKE_Y) * 0.02))
                if (y === LAKE_Y) c = mixc(c, mist, 0.5)
                if (hash01(Math.floor((x + y * 3) / 5) * 31 + y * 7) < 0.12) c = mixc(c, mist, 0.22)
                this.put(x, y, c, layer)
            }
        }
    }

    /**
     * Ridges back to front: trees in clumps and clearings on a rolling line, a flat silhouette darker
     * the nearer it is, fading at its foot into the mist lying before the next. Only the nearest are
     * rimmed. The ridge with the camp leaves a clearing for its fire, and no nearer tree covers it.
     */
    private paintRidges = () => {
        const { x0, y0, w, h } = this.rect
        const mist = hex(this.hour.mist)
        const rimC = hex(this.hour.rim)
        const left = this.rimFromLeft()
        RIDGES.forEach((r, i) => {
            const layer = ridgeLayer(i)
            const tone = mixc(mist, FOREST, r.tone)
            const line = lineOf(r, i)
            const mask = new Uint8Array(w * h)
            const set = (x: number, y: number) => {
                const k = this.index(x, y)
                if (k >= 0) mask[k] = 1
            }
            const has = (x: number, y: number) => {
                const k = this.index(x, y)
                return k >= 0 && mask[k] === 1
            }
            const groundTo = i === 0 ? LAKE_Y : y0 + h
            for (let x = x0; x < x0 + w; x++) for (let y = line(x); y < groundTo; y++) set(x, y)
            if (r.h[1] > 0) {
                let x = x0 - 8 + Math.floor(hash01(i * 97) * r.gap[1])
                let k = 0
                while (x < x0 + w + 10) {
                    const seed = i * 1000 + k++
                    const dens = 0.5 + 0.5 * Math.sin(x * 0.043 + i * 2.3) * Math.sin(x * 0.011 + i)
                    if (dens < 0.18) {
                        x += 8 + Math.round(hash01(seed) * 10)
                        continue
                    }
                    const clear = RIDGES.some((q, qi) => q.fire !== undefined && (qi === i ? Math.abs(x - q.fire) < 8 : qi < i && Math.abs(x - q.fire) < 16))
                    if (!clear) {
                        const tall = hash01(seed * 11) < 0.1 ? 1.4 : 1
                        const th = Math.round((r.h[0] + hash01(seed * 3 + 1) * (r.h[1] - r.h[0])) * tall * (0.75 + 0.35 * dens))
                        const kind = hash01(seed * 7)
                        if (kind < 0.18 && i >= 1) roundShape(x, line(x) + 2, Math.round(th * 0.8), seed, set)
                        else pineShape(x, line(x) + 2, th, seed, set, kind > 0.85 ? 0.72 : 1)
                    }
                    x += Math.round(r.gap[0] + hash01(seed * 5 + 2) * (r.gap[1] - r.gap[0]) * (1.3 - dens))
                }
            }
            if (r.near) {
                // Tall pines at the edge away from the moon, and grass standing up against the mist.
                const right = x0 + w
                const tall: [number, number][] = left ? [[right - 8, 120], [right - 30, 84], [x0 + 16, 62]] : [[x0 + 8, 116], [x0 + 30, 82], [right - 14, 64]]
                tall.forEach(([cx, th], j) => pineShape(cx, line(cx) + 4, th, 7000 + j, set))
                for (let x = x0; x < x0 + w; x++) {
                    const tuft = Math.round(1 + 2.5 * Math.max(0, Math.sin(x * 0.37) + Math.sin(x * 0.13 + 1)) * hash01(x * 5 + 3))
                    for (let k = 1; k <= tuft; k++) if (hash01(x * 31 + k) > 0.25) set(x, line(x) - k)
                }
            }
            const next = RIDGES[i + 1] ? lineOf(RIDGES[i + 1], i + 1) : undefined
            for (let y = y0; y < y0 + h; y++) {
                for (let x = x0; x < x0 + w; x++) {
                    if (!has(x, y)) continue
                    let c = tone
                    if (r.rim) {
                        const side = left ? !has(x - 1, y) : !has(x + 1, y)
                        const top = !has(x, y - 1)
                        if (top && side) c = mixc(tone, rimC, r.rim * 1.3)
                        else if (side) c = mixc(tone, rimC, r.rim * 0.55)
                    }
                    if (r.mist) {
                        const l = line(x)
                        const span = next ? Math.max(6, next(x) - l) : 18
                        const steps = Math.floor(clamp01((y - l - 1) / span) * 4 + (bayer(x, y) - 0.5) * 0.9)
                        if (steps > 0) c = mixc(c, mist, Math.min(4, steps) * 0.24 * r.mist)
                    }
                    this.put(x, y, c, layer)
                }
            }
            if (i === 0) this.paintLake(layer)
        })
    }

    /**
     * How far each pixel is from the firelight, 0 at it and 1 far off: the order the view dissolves
     * away in when the chapter begins, the firelight last. Worked out once, the first time it is asked.
     */
    distanceFromFire = (): Float32Array => {
        if (this.fireDistance) return this.fireDistance
        const { x0, y0, w, h } = this.rect
        const d = new Float32Array(w * h)
        for (let j = 0, i = 0; j < h; j++) for (let k = 0; k < w; k++, i++) d[i] = Math.min(1, Math.hypot(x0 + k - this.fire.x, (y0 + j - this.fire.y + 10) * 1.6) / 300)
        return (this.fireDistance = d)
    }

    // ---- what moves -------------------------------------------------------------------------------

    /**
     * The picture at `nowMs` into `out` (the rect's size): the still scene plus a star or two
     * flickering, the moon's path glinting on the lake, the firelight breathing and its smoke rising.
     */
    paint = (out: Uint32Array, nowMs: number) => {
        out.set(this.base)
        this.paintTwinkle(out, nowMs)
        this.paintGlints(out, nowMs)
        this.paintFire(out, nowMs)
    }

    /** Lay `c` over the pixel at `alpha`, only where what is there is no nearer than `layer`. */
    private over = (out: Uint32Array, x: number, y: number, c: RGB, alpha: number, layer: number) => {
        const i = this.index(x, y)
        if (i < 0 || this.layer[i] > layer || alpha <= 0) return
        out[i] = alpha >= 1 ? pack(c) : pack(mixc(unpack(out[i]), c, alpha))
    }
    private add = (out: Uint32Array, x: number, y: number, c: RGB, k: number, layer: number) => {
        const i = this.index(x, y)
        if (i < 0 || this.layer[i] !== layer || k <= 0) return
        const u = unpack(out[i])
        out[i] = pack([u[0] + c[0] * k, u[1] + c[1] * k, u[2] + c[2] * k])
    }

    private paintTwinkle = (out: Uint32Array, nowMs: number) => {
        for (const s of this.stars) {
            const p = Math.sin(nowMs / (900 + s.seed * 37) + s.seed * 2.1)
            if (p > 0.985) this.over(out, s.x, s.y, hex(0xfffbe8), 1, LAYER_SKY)
            else if (p < -0.97) this.over(out, s.x, s.y, this.ramp[1], 0.6, LAYER_SKY)
        }
    }

    /** The moon's path on the water: short level dashes that come and go, wider nearer the shore. */
    private paintGlints = (out: Uint32Array, nowMs: number) => {
        const mx = this.hour.moon[0]
        const c = mixc(hex(0xd8d4c2), hex(this.hour.mist), 0.3)
        const lakeLayer = ridgeLayer(0)
        for (let y = LAKE_Y + 2, row = 0; y < LAKE_Y + 40; y += 2, row++) {
            const half = 3 + (y - LAKE_Y) * 1.1
            const tick = Math.floor(nowMs / (520 + row * 40))
            for (let d = 0; d < 3; d++) {
                if (hash01(row * 131 + d * 17 + tick * 977) > 0.75) continue
                const x0 = Math.round(mx + (hash01(row * 71 + d * 5 + tick * 13) - 0.5) * 2 * half)
                const len = 1 + Math.floor(hash01(row * 3 + d + tick) * 4)
                for (let x = x0; x < x0 + len; x++) {
                    const i = this.index(x, y)
                    if (i < 0 || this.layer[i] !== lakeLayer) continue
                    this.over(out, x, y, c, 0.55 * (1 - Math.abs(x - mx) / (half + 2)), lakeLayer)
                }
            }
        }
    }

    /** The breath of the fire: one flicker for its light, its flame and its glow. */
    flickerAt = (nowMs: number) => 0.78 + 0.12 * Math.sin(nowMs / 173) + 0.1 * Math.sin(nowMs / 61 + 1.3)

    private paintFire = (out: Uint32Array, nowMs: number) => {
        const { x: fx, y: fy, layer } = this.fire
        const flick = this.flickerAt(nowMs)
        // The smoke: a thin moonlit thread leaning with the air, behind the nearer ridges. Each puff
        // is born on a 45 ms beat and lives ~6 s, so its place is a function of the clock alone.
        const smoke = mixc(hex(this.hour.mist), hex(0xc4b59f), 0.45)
        const BEAT = 45
        const newest = Math.floor(nowMs / BEAT)
        for (let k = newest; k > newest - 160; k--) {
            const life = 5200 + hash01(k * 3 + 1) * 1800
            const a = (nowMs - k * BEAT) / life
            if (a < 0 || a >= 1) continue
            const y = Math.round(fy - 3 - a * 74)
            const x = Math.round(fx + (hash01(k * 5 + 2) - 0.5) * 0.8 + a * a * 26 + Math.sin(a * 7 + hash01(k * 7 + 3) * 6.28) * (0.6 + a * 2.4))
            const alpha = 0.2 * (1 - a) * Math.min(1, a * 10)
            this.over(out, x, y, smoke, alpha, layer)
            if (a > 0.3) this.over(out, x + 1, y, smoke, alpha * 0.6, layer)
            if (a > 0.6) this.over(out, x - 1, y, smoke, alpha * 0.4, layer)
        }
        // Its light on the trees and ground around it, in stepped rings.
        for (let dy = -12; dy <= 6; dy++) {
            for (let dx = -16; dx <= 16; dx++) {
                const x = fx + dx, y = fy + dy
                const d = Math.hypot(dx / 13, dy / (dy < 0 ? 10 : 5)) + (bayer(x, y) - 0.5) * 0.18
                const w = d < 0.3 ? 0.5 : d < 0.62 ? 0.2 : d < 1 ? 0.07 : 0
                if (w) this.add(out, x, y, [255, 92, 26], w * flick, layer)
            }
        }
        // The flame itself: three or four pixels.
        this.over(out, fx, fy, hex(0xffa749), 1, layer)
        this.over(out, fx - 1, fy, hex(0xe8501a), 0.9, layer)
        this.over(out, fx + 1, fy, hex(0xff7b3a), 0.8, layer)
        this.over(out, fx, fy - 1, flick > 0.9 ? hex(0xfff1b0) : hex(0xffc93c), 1, layer)
        if (flick > 0.82) this.over(out, fx + (Math.sin(nowMs / 90) > 0 ? 1 : 0), fy - 2, hex(0xff8f1f), 0.8, layer)
    }
}
